import React from 'react';
import PropTypes from 'prop-types';
import { connect } from '@gasbuddy/react';
import { Button, Loader } from '@gasbuddy/react-components';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Dropdown as RSDropdown,
  DropdownToggle as RSDropdownToggle,
  DropdownMenu as RSDropdownMenu,
  DropdownItem as RSDropdownItem,
} from 'reactstrap';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';

import { ComparisonMetricTypes } from '../../../lib/constants/enums';
import styles from './styles.css';
import sharedStyles from '../../styles/shared.css';
import { setComparisonMetric } from '../../actions';
import ComparisonMetricPropType from '../../utils/prop-types/comparison-metric';

const { api, store, setQueryParameter } = require('../../../lib/bputils').default;
const { gTagEvent } = require('../../../lib/global-site-tag').default;

const COMPARISON_METRICS = [
  {
    key: ComparisonMetricTypes.priorPeriod,
    text: ComparisonMetricTypes.priorPeriod,
    metricApiName: 'prior-period',
    metricApiPath: 'stations/comparison/prior-period',
  },
  {
    key: ComparisonMetricTypes.myStations,
    text: ComparisonMetricTypes.myStations,
    target: '',
    metricApiName: 'station-lists',
    metricApiPath: 'stations/comparison/station-lists',
  },
  {
    key: ComparisonMetricTypes.nearbyStations,
    text: ComparisonMetricTypes.nearbyStations,
    metricApiName: 'nearby',
    metricApiPath: 'stations/comparison/nearby',
  },
  {
    key: ComparisonMetricTypes.industry,
    text: ComparisonMetricTypes.industry,
    metricApiName: 'industry',
    metricApiPath: 'industry',
  },
];

const defaultComparisonMetric = COMPARISON_METRICS[0];

class ComparisonMetricSelector extends React.Component {
  static propTypes = {
    id: PropTypes.string,
    showBrandMetric: PropTypes.bool,
    onChange: PropTypes.func,
    dispatchSetComparisonMetric: PropTypes.func,
    className: PropTypes.string,
    doNotDispatchChanges: PropTypes.bool,
    currentComparisonMetric: ComparisonMetricPropType,
    gTagTitle: PropTypes.string.isRequired,
    doNotStoreChanges: PropTypes.bool,
  };

  static defaultProps = {
    id: '',
    showBrandMetric: false,
    onChange: null,
    dispatchSetComparisonMetric: () => { },
    className: '',
    doNotDispatchChanges: false,
    currentComparisonMetric: null,
    doNotStoreChanges: false,
  };

  constructor(props) {
    super(props);
    this.sendGTagEvent = false;
    this.state = {
      isOpen: false,
      selectedComparisonMetric: this.props.currentComparisonMetric || this.setDefaultComparison(),
      allStationsListId: null,
      selectedBrand: 0,
      brands: [],
      activeItemId: '',
    };
  }

  componentDidMount = async () => {
    document.addEventListener('mousedown', this.globalOnClickHandler);
    await this.initialize();
  }

  componentDidUpdate = (prevProps, prevState) => {
    const { allStationsListId, brands } = this.state;
    const { currentComparisonMetric } = this.props;
    if (prevState.allStationsListId !== allStationsListId) {
      COMPARISON_METRICS[1].target = allStationsListId;
    }

    if (prevProps.currentComparisonMetric?.text !== currentComparisonMetric?.text) {
      if (currentComparisonMetric.metricApiName !== 'brands') {
        const comparison = COMPARISON_METRICS.find(cm => cm.metricApiName === currentComparisonMetric.metricApiName);
        this.setState({ selectedComparisonMetric: comparison });
      } else {
        const brand = brands.find(b => b.id === Number(currentComparisonMetric.target));
        const comparison = {
          ...currentComparisonMetric,
          text: brand.display_name,
          target: brand.id.toString(),
        };
        const selectedBrand = {
          value: brand.id,
          label: brand.display_name,
        };
        this.setState({ selectedComparisonMetric: comparison, selectedBrand });
      }
    }
  }

  componentWillUnmount = () => {
    document.removeEventListener('mousedown', this.globalOnClickHandler);
  }

  setDefaultComparison = () => {
    if (!this.props.doNotStoreChanges) {
      store.set('mostRecentComparisonMetric', defaultComparisonMetric);
    }
    return defaultComparisonMetric;
  }

  getStationLists = async () => api.get({
    path: '/station-lists',
    requestId: `stationListSelectLoadLists${this.props.id}`,
  })

  getBrands = async () => api.get({
    path: '/brands',
    requestId: 'getAllBrands',
  })

  initialize = async () => {
    const brandMetric = {};
    const { showBrandMetric, currentComparisonMetric } = this.props;

    // Station Lists are needed for "My Stations" & "Other Station Lists"
    const stationLists = await this.getStationLists();
    const allStationsListId = stationLists.find(list => list.list_type === 'allstations').id;

    let recentComparisonMetric = currentComparisonMetric
      ? COMPARISON_METRICS.find(c => c.metricApiName === currentComparisonMetric.metricApiName) : store.get('mostRecentComparisonMetric');

    if (currentComparisonMetric?.metricApiName === 'brands') {
      recentComparisonMetric = currentComparisonMetric;
    }

    if (!recentComparisonMetric) {
      recentComparisonMetric = this.setDefaultComparison();
    }

    let { brands } = this.state;
    if (showBrandMetric) {
      brands = await this.getBrands();
      brandMetric.brands = brands;
      const firstBrand = brandMetric.brands[0];
      let selectedBrand = {
        value: firstBrand.id,
        label: firstBrand.display_name,
      };
      if (recentComparisonMetric?.metricApiName === 'brands') {
        const brand = brands.find(b => b.id === Number(currentComparisonMetric.target));
        recentComparisonMetric.text = brand.display_name;
        recentComparisonMetric.target = brand.id.toString();
        selectedBrand = {
          value: recentComparisonMetric.target,
          label: recentComparisonMetric.text,
        };
        recentComparisonMetric.metricApiPath = `brands/${recentComparisonMetric.target}`;
      }

      brandMetric.selectedBrand = selectedBrand;
    }

    COMPARISON_METRICS[1].target = allStationsListId;
    this.setState(() => ({
      selectedComparisonMetric: recentComparisonMetric,
      ...brandMetric,
      allStationsListId,
      brands,
      selectedBrand: brandMetric.selectedBrand,
    }));

    this.updateComparisonMetric(recentComparisonMetric, brandMetric.selectedBrand, allStationsListId, true);
  }

  getCurrentComparisonMetric = () => this.selectedComparisonMetric;

  closeDropdown = (newComparisonMetric) => {
    if (!newComparisonMetric) {
      this.setState(() => ({
        isOpen: false,
        activeItemId: '',
      }));
      return;
    }

    this.setState(prevState => ({
      isOpen: false,
      selectedComparisonMetric: newComparisonMetric || prevState.selectedComparisonMetric,
      activeItemId: '',
    }));
  }

  toggle = () => {
    const { isOpen } = this.state;
    if (!isOpen) {
      this.setState(() => ({
        isOpen: true,
      }));
    }
  }

  updateComparisonMetric = (selectedComparisonMetric, selectedBrand, allStationsListId, shouldDispatch) => {
    const { dispatchSetComparisonMetric, onChange, gTagTitle, doNotStoreChanges } = this.props;

    if (selectedComparisonMetric.metricApiName === 'brands') {
      selectedComparisonMetric.target = String(selectedBrand.value);
      selectedComparisonMetric.metricApiPath = `brands/${selectedBrand.value}`;
      selectedComparisonMetric.text = selectedBrand.label;
    }
    if (selectedComparisonMetric.key === ComparisonMetricTypes.myStations) {
      selectedComparisonMetric.text = ComparisonMetricTypes.myStations;
      selectedComparisonMetric.target = String(allStationsListId);
      selectedComparisonMetric.metricApiPath = `stations/comparison/station-lists/${allStationsListId}`;
    }

    if (!doNotStoreChanges) {
      store.set('mostRecentComparisonMetric', selectedComparisonMetric);
      // set parameter for comparisonListId
      setQueryParameter('comparisonMetric', selectedComparisonMetric.text);
      setQueryParameter('comparisonTarget', selectedComparisonMetric.target);
    }

    if (shouldDispatch) {
      dispatchSetComparisonMetric(selectedComparisonMetric);
    }

    if (this.sendGTagEvent) {
      const metricName = selectedComparisonMetric.metricApiName === 'brands' ? 'Brand' : selectedComparisonMetric.text;
      gTagEvent(gTagTitle, `Selected Comparison Metric - ${metricName}`, { metric: selectedComparisonMetric.text });
    }
    this.sendGTagEvent = true;

    if (onChange) {
      onChange(selectedComparisonMetric);
    }

    return selectedComparisonMetric;
  }

  handleOnApplyClick = (e) => {
    const { doNotDispatchChanges } = this.props;
    const { selectedComparisonMetric, selectedBrand, allStationsListId } = this.state;

    const updatedComparisonMetric = this.updateComparisonMetric(selectedComparisonMetric, selectedBrand, allStationsListId, !doNotDispatchChanges);
    this.closeDropdown(updatedComparisonMetric);
    e.preventDefault();
  }

  handleOnCancelClick = (e) => {
    this.closeDropdown();
    e.preventDefault();
  }

  handleOnComparisonMetricItemClick = (e) => {
    const elementDataSet = e.currentTarget.dataset;
    const { selectedId, selectedPath, selectedName, selectedKey } = elementDataSet;
    const { selectedText } = elementDataSet;
    this.setState(() => ({
      selectedComparisonMetric: {
        text: selectedText,
        key: selectedKey,
        target: selectedId || '',
        metricApiName: selectedName,
        metricApiPath: selectedPath,
      },
      activeItemId: selectedName,
    }));
  }

  globalOnClickHandler = (event) => {
    const { isOpen } = this.state;
    if (isOpen) {
      if (this.comparisonDropdownContainer && !this.comparisonDropdownContainer.getContainer().contains(event.target)) {
        this.closeDropdown();
      }
    }
  }

  handleOnBrandChange = (changedBrand) => {
    const { selectedBrand } = this.state;
    if (changedBrand.value !== selectedBrand.value) {
      this.setState(() => ({
        selectedBrand: changedBrand,
      }));
    }
  }

  getBrandOptions = () => {
    const { brands } = this.state;

    return brands.map(brand => ({
      value: brand.id,
      label: brand.display_name,
    }));
  }

  render = () => {
    const { showBrandMetric, className } = this.props;
    const { isOpen, selectedBrand } = this.state;
    const { selectedComparisonMetric } = this.state;
    let { activeItemId } = this.state;

    if (activeItemId === '') {
      activeItemId = selectedComparisonMetric.metricApiName;
    }
    const currentComparisonText = selectedComparisonMetric.text;
    const selectedBrandId = selectedBrand ? selectedBrand.value : '';

    return (
      <RSDropdown
        className={`${sharedStyles.rsDropdown} ${styles.rsDropdownOverride} ${className}`}
        isOpen={isOpen}
        toggle={this.toggle}
        ref={(el) => { this.comparisonDropdownContainer = el; }}
      >
        <RSDropdownToggle
          caret
          className={sharedStyles.rsDropdownToggle}
          style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
        >
          {currentComparisonText?.length ? <span>{currentComparisonText}</span> : <Loader size="xs" className={styles.dropdowndLoader} />}
          <FontAwesomeIcon className={sharedStyles.rsDropdownChevron} icon={isOpen ? faChevronUp : faChevronDown} />
        </RSDropdownToggle>

        <RSDropdownMenu className={sharedStyles.rsDropdownMenu}>
          {COMPARISON_METRICS.map(comparison => (
            <RSDropdownItem
              className={activeItemId === comparison.metricApiName ? styles.activeItem : ''}
              onClick={this.handleOnComparisonMetricItemClick}
              data-selected-name={comparison.metricApiName}
              data-selected-path={`${comparison.metricApiPath}${comparison.target ? `/${comparison.target}` : ''}`}
              data-selected-text={comparison.text}
              data-selected-key={comparison.key}
            >
              {comparison.key}
            </RSDropdownItem>
          ))}
          {
            showBrandMetric
            && (
              <RSDropdownItem
                className={activeItemId === 'brands' ? styles.activeItem : ''}
                onClick={this.handleOnComparisonMetricItemClick}
                data-selected-id={selectedBrandId}
                data-selected-name="brands"
                data-selected-path={`brands/${selectedBrandId}`}
                data-selected-text={ComparisonMetricTypes.brand}
                data-selected-key={ComparisonMetricTypes.brand}
              >
                {ComparisonMetricTypes.brand}
                <Select
                  className={sharedStyles.rsSelect}
                  classNamePrefix="react-select"
                  options={this.getBrandOptions()}
                  value={selectedBrand}
                  onChange={this.handleOnBrandChange}
                />
              </RSDropdownItem>
            )
          }
          <div className={styles.buttons}>
            <Button
              type="submit"
              rounded
              size="sm"
              color="red"
              onClick={this.handleOnApplyClick}
              className={sharedStyles.pullLeft}
            >
              Apply
            </Button>
            <Button
              type="submit"
              rounded
              size="sm"
              color="red"
              bordered
              onClick={this.handleOnCancelClick}
              className={sharedStyles.pullRight}
            >
              Cancel
            </Button>
          </div>
        </RSDropdownMenu>
      </RSDropdown>
    );
  }
}

const mapStateToProps = (state, props) => ({
  brands: state.brands,
  currentComparisonMetric: props?.currentComparisonMetric ? props.currentComparisonMetric : state.comparisonMetric,
});

const mapDispatchToProps = dispatch => ({
  dispatchSetComparisonMetric: comparisonMetric => dispatch(setComparisonMetric(comparisonMetric)),
});

module.exports = connect(mapStateToProps, mapDispatchToProps)(ComparisonMetricSelector);
